import React, { useState } from "react";
import { Link } from "react-router-dom";
import { useAuth, formatApiError } from "../contexts/AuthContext";
import { Shield, Eye, EyeOff, Loader, KeyRound, Building2 } from "lucide-react";
import { useWorkspaceBranding } from "../lib/useWorkspaceBranding";

export default function LoginPage() {
  const { login } = useAuth();
  const branding = useWorkspaceBranding();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPw, setShowPw] = useState(false);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [needs2FA, setNeeds2FA] = useState(false);
  const [totpCode, setTotpCode] = useState("");
  const [pendingTenantId, setPendingTenantId] = useState(null);
  const [tenantOptions, setTenantOptions] = useState(null);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError(""); setLoading(true);
    try {
      const opts = {};
      if (needs2FA) opts.totp_code = totpCode;
      if (pendingTenantId) opts.tenant_id = pendingTenantId;
      const r = await login(email, password, opts);
      if (r?.select_tenant) {
        setTenantOptions(r.tenants);
      } else if (r?.require_2fa) {
        setNeeds2FA(true);
        setPendingTenantId(r.tenant_id || null);
      }
    } catch (err) {
      setError(formatApiError(err.response?.data?.detail) || "Anmeldung fehlgeschlagen");
    } finally { setLoading(false); }
  };

  const reset = () => {
    setNeeds2FA(false); setTotpCode(""); setPassword(""); setError("");
    setPendingTenantId(null); setTenantOptions(null);
  };

  const pickTenant = async (tenant_id) => {
    setError(""); setLoading(true); setPendingTenantId(tenant_id); setTenantOptions(null);
    try {
      const r = await login(email, password, { tenant_id });
      if (r?.require_2fa) {
        setNeeds2FA(true);
      }
    } catch (err) {
      setError(formatApiError(err.response?.data?.detail));
    } finally { setLoading(false); }
  };

  return (
    <div className="min-h-screen flex items-center justify-center relative overflow-hidden bg-[#0a0e1a]">
      {/* Animated aurora background */}
      <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_30%_20%,hsl(var(--primary)/0.55),transparent_50%),radial-gradient(ellipse_at_70%_60%,hsl(var(--primary)/0.35),transparent_55%),radial-gradient(ellipse_at_50%_90%,#1d4ed8_0%,transparent_45%)]" />
      <div className="absolute inset-0 login-aurora-a" />
      <div className="absolute inset-0 login-aurora-b" />
      {/* Subtle grain / star field */}
      <div className="absolute inset-0 opacity-[0.07] mix-blend-overlay" style={{
        backgroundImage: "radial-gradient(#fff 1px, transparent 1px), radial-gradient(#fff 1px, transparent 1px)",
        backgroundSize: "48px 48px, 24px 24px",
        backgroundPosition: "0 0, 12px 12px",
      }} />
      <div className="absolute inset-0 bg-gradient-to-b from-transparent via-transparent to-black/50" />

      {/* Login Card */}
      <div className="relative z-10 w-full max-w-sm mx-4 animate-in">
        <div className="bg-card border border-border p-8 shadow-xl">
          {/* Logo */}
          <div className="flex items-center gap-3 mb-8">
            {branding?.logo_data_uri ? (
              <img src={branding.logo_data_uri} alt="Logo" className="w-9 h-9 object-contain" data-testid="login-branded-logo" />
            ) : (
              <div className="w-9 h-9 bg-primary flex items-center justify-center">
                <Shield size={18} className="text-primary-foreground" />
              </div>
            )}
            <div className="flex-1 min-w-0">
              <span className="text-2xl font-bold tracking-tight block truncate" style={{ fontFamily: "'Cabinet Grotesk', sans-serif" }} data-testid="login-branded-name">
                {branding?.display_name || "DATON"}
              </span>
              {branding?.display_name && (
                <span className="text-[10px] uppercase tracking-widest font-bold text-muted-foreground">powered by DATON</span>
              )}
            </div>
          </div>

          <h1 className="text-xl font-semibold mb-1">{
            tenantOptions ? "Workspace wählen"
              : needs2FA ? "Bestätigungscode"
              : "Anmelden"
          }</h1>
          <p className="text-sm text-muted-foreground mb-6">
            {tenantOptions ? "Du bist Mitglied in mehreren Workspaces"
              : needs2FA ? "Code aus deiner Authenticator-App eingeben"
              : "IT-Management Dashboard"}
          </p>

          {tenantOptions ? (
            <div className="space-y-2">
              {tenantOptions.map(t => (
                <button key={t.tenant_id} onClick={() => pickTenant(t.tenant_id)}
                  className="w-full flex items-center gap-3 px-4 py-3 bg-card hover:bg-accent border border-border hover:border-primary text-left transition-colors">
                  <Building2 size={16} className="text-primary" />
                  <span className="font-semibold">{t.name}</span>
                </button>
              ))}
              <button onClick={reset} className="w-full text-xs text-muted-foreground hover:text-foreground py-2 mt-2">
                ← Zurück zum Login
              </button>
            </div>
          ) : (
          <form onSubmit={handleSubmit} className="space-y-4">
            {!needs2FA && (
              <>
                <div>
                  <label className="text-xs uppercase tracking-widest font-bold text-muted-foreground block mb-1.5">
                    E-Mail
                  </label>
                  <input
                    data-testid="login-email-input"
                    type="email"
                    value={email}
                    onChange={e => setEmail(e.target.value)}
                    required
                    autoFocus
                    className="w-full px-3 py-2.5 text-sm bg-background border border-input focus:border-primary focus:outline-none transition-colors"
                    placeholder="admin@daton.local"
                  />
                </div>
                <div>
                  <label className="text-xs uppercase tracking-widest font-bold text-muted-foreground block mb-1.5">
                    Passwort
                  </label>
                  <div className="relative">
                    <input
                      data-testid="login-password-input"
                      type={showPw ? "text" : "password"}
                      value={password}
                      onChange={e => setPassword(e.target.value)}
                      required
                      className="w-full px-3 py-2.5 pr-10 text-sm bg-background border border-input focus:border-primary focus:outline-none transition-colors"
                      placeholder="••••••••"
                    />
                    <button
                      type="button"
                      onClick={() => setShowPw(!showPw)}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground transition-colors"
                    >
                      {showPw ? <EyeOff size={16} /> : <Eye size={16} />}
                    </button>
                  </div>
                </div>
              </>
            )}

            {needs2FA && (
              <div>
                <label className="text-xs uppercase tracking-widest font-bold text-muted-foreground block mb-1.5 flex items-center gap-1.5">
                  <KeyRound size={11} /> 6-stelliger Code (oder Recovery-Code)
                </label>
                <input
                  data-testid="login-totp-input"
                  type="text"
                  value={totpCode}
                  onChange={e => setTotpCode(e.target.value)}
                  required
                  autoFocus
                  inputMode="numeric"
                  className="w-full px-3 py-2.5 text-lg font-mono tracking-[0.4em] text-center bg-background border border-input focus:border-primary focus:outline-none transition-colors"
                  placeholder="000000"
                />
                <button type="button" onClick={reset} className="mt-2 text-xs text-muted-foreground hover:text-foreground">
                  ← Zurück zum Login
                </button>
              </div>
            )}

            {error && (
              <div data-testid="login-error" className="text-sm text-destructive bg-destructive/10 px-3 py-2 border border-destructive/20">
                {error}
              </div>
            )}

            <button
              data-testid="login-submit-button"
              type="submit"
              disabled={loading}
              className="w-full py-2.5 bg-primary text-primary-foreground text-sm font-semibold hover:bg-primary/90 disabled:opacity-50 transition-colors flex items-center justify-center gap-2"
            >
              {loading ? <Loader size={16} className="animate-spin" /> : null}
              {loading ? "Anmelden..." : (needs2FA ? "Code prüfen" : "Anmelden")}
            </button>
          </form>
          )}

          {!tenantOptions && !needs2FA && (
            <p className="text-xs text-muted-foreground text-center mt-4">
              Noch keinen Account? <Link to="/signup" data-testid="link-to-signup" className="text-primary hover:underline font-semibold">Workspace erstellen</Link>
            </p>
          )}

          <p className="text-xs text-muted-foreground text-center mt-6">
            DATON v1.0 — IT-Support & Kundenverwaltung
          </p>
        </div>
      </div>
    </div>
  );
}
